import type { ThemeTokens } from '@cogo/react-renderer-v2'
import { lookupPreset } from './shadcn-presets'
import {
  BASE_COLOR_OPTIONS,
  CHART_OPTIONS,
  FONT_OPTIONS,
  RADIUS_OPTIONS,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  type DesignSystemState,
} from './design-system-options'

/**
 * Compute AppV2 ThemeTokens from the 6-axis design-system state.
 *
 * Kept outside the React hook so headless Studio scripts can plan/gate
 * generation without importing the Zustand store or browser-only shell code.
 */
export function computeThemeTokens(state: DesignSystemState): ThemeTokens {
  const preset = lookupPreset({
    style: state.style,
    baseColor: state.baseColor,
    theme: state.theme,
    chartColor: state.chart,
    radius: state.radius,
    font: state.font,
  })
  const { radius: radiusValue, ...colors } = preset.cssVars.light
  return { colors, radius: radiusValue, fonts: preset.fonts }
}

/** Parse the exact persisted six-axis state; partial/unknown values are stale. */
export function parseDesignSystemState(value: unknown): DesignSystemState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const candidate = value as Record<string, unknown>
  const allowed = {
    style: STYLE_OPTIONS,
    baseColor: BASE_COLOR_OPTIONS,
    theme: THEME_OPTIONS,
    chart: CHART_OPTIONS,
    radius: RADIUS_OPTIONS,
    font: FONT_OPTIONS,
  } as const
  for (const [key, options] of Object.entries(allowed)) {
    const selected = candidate[key]
    if (
      typeof selected !== 'string' ||
      !options.some((option) => option.value === selected)
    ) {
      return null
    }
  }
  return {
    style: candidate.style as DesignSystemState['style'],
    baseColor: candidate.baseColor as DesignSystemState['baseColor'],
    theme: candidate.theme as DesignSystemState['theme'],
    chart: candidate.chart as DesignSystemState['chart'],
    radius: candidate.radius as DesignSystemState['radius'],
    font: candidate.font as DesignSystemState['font'],
  }
}

const RADIUS_KEY = '--cogo-radius'
const FONT_SANS_KEY = '--cogo-font-sans'
const FONT_MONO_KEY = '--cogo-font-mono'
const FONT_HEADING_KEY = '--cogo-font-heading'

/**
 * Flatten renderer-native theme tokens for the AppSpec's governed generator
 * channel. Reserved `--cogo-*` keys preserve non-color values without
 * letting them be mistaken for semantic color slots.
 */
export function encodeAppSpecDesignSystemTokens(
  state: DesignSystemState,
): Record<string, string> {
  const tokens = computeThemeTokens(state)
  return {
    ...(tokens.colors ?? {}),
    ...(typeof tokens.radius === 'string'
      ? { [RADIUS_KEY]: tokens.radius }
      : {}),
    ...(tokens.fonts?.sans ? { [FONT_SANS_KEY]: tokens.fonts.sans } : {}),
    ...(tokens.fonts?.mono ? { [FONT_MONO_KEY]: tokens.fonts.mono } : {}),
    ...(tokens.fonts?.heading
      ? { [FONT_HEADING_KEY]: tokens.fonts.heading }
      : {}),
  }
}

/** Decode the flat AppSpec transport into the renderer's native token shape. */
export function decodeAppSpecDesignSystemTokens(
  value: Record<string, string>,
): ThemeTokens {
  const colors: Record<string, string> = {}
  const legacyRadius: Record<string, string> = {}
  for (const [key, token] of Object.entries(value)) {
    if (key.startsWith('radius-')) {
      legacyRadius[key.slice('radius-'.length)] = token
    } else if (!key.startsWith('--cogo-')) {
      colors[key] = token
    }
  }
  const radius = value[RADIUS_KEY]
  const sans = value[FONT_SANS_KEY] ?? value['--cogo-font-body']
  const mono = value[FONT_MONO_KEY]
  const heading = value[FONT_HEADING_KEY]
  return {
    ...(Object.keys(colors).length > 0 ? { colors } : {}),
    ...(radius
      ? { radius }
      : Object.keys(legacyRadius).length > 0
        ? { radius: legacyRadius }
        : {}),
    ...(sans || mono || heading
      ? {
          fonts: {
            ...(sans ? { sans } : {}),
            ...(mono ? { mono } : {}),
            ...(heading ? { heading } : {}),
          },
        }
      : {}),
  }
}
